/**
 * SlashCommandSuggestions Component (Issue #302)
 *
 * Autocomplete suggestions for slash commands.
 * Shows when user types '/' in the chat input.
 */
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  alpha,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedIcon from '@mui/icons-material/Verified';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { SLASH_COMMANDS, SlashCommand } from '../../types/chat';

// Icon mapping for commands
const COMMAND_ICONS: Record<string, React.ReactNode> = {
  help: <HelpOutlineIcon />,
  settings: <SettingsIcon />,
  ask: <QuestionAnswerIcon />,
  plan: <ArchitectureIcon />,
  drbfm: <AssessmentIcon />,
  verification: <VerifiedIcon />,
};

interface SlashCommandSuggestionsProps {
  inputValue: string;
  anchorEl: HTMLElement | null;
  onSelectCommand: (command: SlashCommand) => void;
  onClose: () => void;
  isVisible?: boolean; // Parent-controlled visibility (e.g., for Escape key handling)
}

const SlashCommandSuggestions: React.FC<SlashCommandSuggestionsProps> = ({
  inputValue,
  anchorEl,
  onSelectCommand,
  onClose,
  isVisible = true, // Default to true for backwards compatibility
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter commands based on input
  const filteredCommands = useMemo(() => {
    if (!inputValue.startsWith('/')) {
      return [];
    }

    const query = inputValue.toLowerCase().slice(1); // Remove leading '/'

    if (query === '') {
      return SLASH_COMMANDS;
    }

    return SLASH_COMMANDS.filter(
      (cmd) =>
        cmd.command.slice(1).startsWith(query) ||
        cmd.label.toLowerCase().includes(query) ||
        cmd.modeId.includes(query)
    );
  }, [inputValue]);

  // Consider parent-controlled visibility in addition to filtered commands
  const isOpen = filteredCommands.length > 0 && Boolean(anchorEl) && isVisible;

  // Reset selection when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          if (filteredCommands[selectedIndex]) {
            event.preventDefault();
            onSelectCommand(filteredCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, filteredCommands, selectedIndex, onSelectCommand, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="top-start"
      style={{ zIndex: 1300 }}
    >
      <Paper
        elevation={8}
        sx={{
          minWidth: 280,
          maxWidth: 360,
          borderRadius: 2,
          mb: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {t('chat.commands')}
          </Typography>
        </Box>
        <List dense sx={{ py: 0.5, maxHeight: 300, overflow: 'auto' }}>
          {filteredCommands.map((cmd, index) => {
            const isSelected = index === selectedIndex;
            const icon = COMMAND_ICONS[cmd.modeId] || <QuestionAnswerIcon />;

            return (
              <ListItemButton
                key={cmd.command}
                onClick={() => onSelectCommand(cmd)}
                selected={isSelected}
                sx={{
                  borderRadius: 1,
                  mx: 0.5,
                  my: 0.25,
                  '&.Mui-selected': {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.15),
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          color: 'primary.main',
                        }}
                      >
                        {cmd.command}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 500 : 400,
                        }}
                      >
                        {t(`chat.modes.${cmd.modeId}.name`, cmd.label)}
                      </Typography>
                    </Box>
                  }
                  secondary={t(`chat.modes.${cmd.modeId}.description`, cmd.description)}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { opacity: 0.7 },
                  }}
                />
                {isSelected && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 1,
                    }}
                  >
                    <KeyboardReturnIcon
                      sx={{ fontSize: 14, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t('chat.enterKey')}
                    </Typography>
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
    </Popper>
  );
};

export default SlashCommandSuggestions;
